"use client";

import React from "react";
import { FileCheck2, Palette, Package, ClipboardCheck, Maximize2, House as Home, Compass, TrendingUp, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface TimelineFactor {
  step: string;
  title: string;
  duration: string;
  description: string;
  icon: React.ElementType;
}
interface TriggerItem {
  title: string;
  description: string;
  icon: React.ElementType;
}
const TIMELINE_FACTORS: TimelineFactor[] = [{
  step: "01",
  title: "Design finalization",
  duration: "2–4 weeks",
  description: "Architectural floor planning, 3D renderings, and precise finish selection for cabinetry, quartz or marble slabs, and custom fixtures.",
  icon: Palette
}, {
  step: "02",
  title: "City of Brookhaven permitting",
  duration: "2–4 weeks",
  description: "Submittal and review through Brookhaven Community Development to ensure structural, electrical, and plumbing plans strictly conform to municipal codes.",
  icon: FileCheck2
}, {
  step: "03",
  title: "Material & cabinet ordering lead times",
  duration: "6–10 weeks",
  description: "Custom millwork, custom cabinetry, appliances, and imported stone fabrication sourced prior to demo to protect your on-site construction schedule.",
  icon: Package
}, {
  step: "04",
  title: "Inspections",
  duration: "Key project milestones",
  description: "Rough-in and final municipal inspections conducted at pivotal stages to certify wiring, venting, plumbing, and structural integrity.",
  icon: ClipboardCheck
}];
const REMODEL_TRIGGERS: TriggerItem[] = [{
  title: "Kitchen feels too small",
  description: "Prep surfaces conflict with everyday movement, storage is overflowing, and more than one cook quickly creates tight pinch points.",
  icon: Maximize2
}, {
  title: "Bought an older Brookhaven home with dated kitchen",
  description: "Classic neighborhood architecture in Historic Brookhaven, Ashford Park, or Brittany that needs a kitchen brought up to modern living standards.",
  icon: Home
}, {
  title: "Want an open layout",
  description: "A closed-off galley or compartmentalized floor plan that disconnects the culinary zone from your family room and entertaining areas.",
  icon: Compass
}, {
  title: "Preparing to sell",
  description: "Updating high-traffic surfaces and cabinetry to maximize resale appeal and command premium equity in the North Atlanta housing market.",
  icon: TrendingUp
}, {
  title: "Outdated design",
  description: "Honey oak cabinetry, failing laminate countertops, low-hanging soffits, or tired tile patterns that no longer match your personal style.",
  icon: Sparkles
}];
export default function KitchenTimeline() {
  return <section id="kitchenremodeltimelineandtriggers" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Part 1: Timeline Factors */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <p className="text-sm font-semibold tracking-wide uppercase text-primary mb-3">
                Planning & Execution
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground leading-tight mb-6">
                Understanding your kitchen remodel timeline
              </h2>
              <p className="text-base text-foreground/80 leading-relaxed mb-8">
                A seamless remodel depends on proactive preparation before hammers swing. We orchestrate Brookhaven municipal permits, lead times, and structural trades to safeguard your calendar.
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation_craftsmanship.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation_craftsmanship.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation_craftsmanship.png-wvc-sizes" alt="Detailed kitchen cabinetry and custom island installation during renovation" loading="lazy" decoding="async" className="w-full h-72 sm:h-80 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent flex items-end p-6">
                <p className="text-secondary-foreground text-sm font-medium">
                  Brookhaven Elevation Renovations – Custom Cabinetry & Detail Installation
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col gap-5">
            {TIMELINE_FACTORS.map((factor, i) => {
            const Icon = factor.icon;
            return <div key={factor.step} data-index={i} className="group relative bg-card text-card-foreground p-6 sm:p-7 rounded-xl border border-border shadow-xs hover:shadow-md transition-shadow duration-200">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                    <div className="flex items-center justify-between sm:flex-col sm:items-center sm:justify-start gap-2 shrink-0">
                      <div className="size-12 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                        <Icon className="size-6 text-primary" aria-hidden="true" />
                      </div>
                      <span className="font-mono text-xs font-semibold text-foreground/60 tracking-wider">
                        PHASE {factor.step}
                      </span>
                    </div>

                    <div className="flex-1">
                      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                        <h3 className="text-lg sm:text-xl font-semibold text-foreground">
                          {factor.title}
                        </h3>
                        <span className="inline-block text-xs font-medium px-2.5 py-1 rounded-md bg-muted text-foreground/80 border border-border">
                          {factor.duration}
                        </span>
                      </div>
                      <p className="text-sm text-foreground/75 leading-relaxed">
                        {factor.description}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="my-16 lg:my-24">
          <Separator className="bg-border" />
        </div>

        {/* Part 2: Is It Time to Remodel Your Kitchen? */}
        <div>
          <div className="max-w-3xl mb-12">
            <p className="text-sm font-semibold tracking-wide uppercase text-primary mb-3">
              Home Life Moments
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground leading-tight mb-4">
              Is it time to remodel your kitchen?
            </h2>
            <p className="text-base text-foreground/80 leading-relaxed">
              Every home reaches a turning point where daily routines outgrow existing counters and cabinets. Here are the common moments our Brookhaven clients know it is time for a change.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REMODEL_TRIGGERS.map((trigger, i) => {
            const TriggerIcon = trigger.icon;
            return <Card key={trigger.title} data-index={i} className="border border-border shadow-xs hover:border-primary/40 hover:shadow-md transition-all duration-200 bg-card">
                  <CardHeader className="gap-3">
                    <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <TriggerIcon className="size-5" aria-hidden="true" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground">
                      {trigger.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-foreground/75 text-sm leading-relaxed">
                      {trigger.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}

            {/* Visual CTA Card fitting the 6th slot */}
            <div className="relative rounded-xl overflow-hidden border border-border bg-secondary text-secondary-foreground p-6 sm:p-8 flex flex-col justify-between">
              <div className="relative z-10">
                <span className="text-xs font-mono uppercase tracking-wider text-primary font-semibold">
                  Local Brookhaven Experience
                </span>
                <h3 className="text-2xl font-serif font-semibold mt-3 mb-2 text-secondary-foreground">
                  Ready to map out your kitchen?
                </h3>
                <p className="text-sm text-secondary-foreground/80 leading-relaxed mb-6">
                  Schedule a thorough in-home consultation to review structural walls, mechanicals, and layout possibilities.
                </p>
              </div>

              <div className="relative z-10">
                <Button asChild variant="default" size="lg" className="w-full sm:w-auto font-medium">
                  <Link to="/ContactRequestAnEstimatePage">
                    Schedule an estimate
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}